import { useState } from "react";
import { COLUMNS, columnOf, dueText, deliveryText, projectLabel, todayISO } from "@/lib/orders";
import { formatCZK, formatCZKCompact } from "@/lib/format";
import { ModeIcon, Warn, Amount, orderAmount } from "./OrderBits";

function Card({ o, today, money, am, drag, dragging, onOpen, onDragStart, onDragEnd }) {
  const pas = (o.lines || []).some((l) => l.pasport_item_id);
  const red = o.late || o.problem;
  return (
    <article draggable={drag} onDragStart={(e) => onDragStart(e, o)} onDragEnd={onDragEnd}
      onClick={() => onOpen(o)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(o); }} tabIndex={0} role="button"
      aria-label={`${o.supplier_name}, ${o.title}`} data-testid={`order-card-${o.id}`}
      className={`flex flex-col gap-1 bg-card rounded-[14px] border border-border/70 p-3 text-left cursor-pointer outline-none
        shadow-[0_1px_2px_rgba(20,24,33,.06)] hover:shadow-[0_6px_16px_rgba(20,24,33,.10)] focus-visible:ring-2 focus-visible:ring-brand transition-shadow motion-reduce:transition-none
        ${red ? "border-l-[3px] border-l-destructive" : o.no_term || o.pickup_wait ? "border-l-[3px] border-l-amber-400" : ""} ${dragging ? "opacity-40" : ""}`}>
      <Warn o={o} today={today} />
      <div className="flex items-start gap-2">
        <b className="flex-1 min-w-0 text-[14.5px] font-bold leading-snug truncate">{o.supplier_name}</b>
        {money && <Amount o={o} am={am} className="text-[13.5px] font-bold" />}
      </div>
      <span className="text-[13px] truncate">{o.title}</span>
      <span className="flex items-center gap-1.5 flex-wrap text-[12px] text-muted-foreground">
        <span className="font-semibold text-foreground">{projectLabel(o)}</span>
        {pas && <span className="px-1.5 rounded-full bg-secondary text-[11px] font-semibold">pasport</span>}
      </span>
      <span className="flex items-center justify-between gap-2 text-[12px] text-muted-foreground mt-0.5">
        <span className="flex items-center gap-1 min-w-0"><ModeIcon o={o} size={13} /><span className="truncate">{deliveryText(o)}</span></span>
        <span className={`whitespace-nowrap font-semibold ${o.late ? "text-destructive" : o.due_date === today ? "pp-ink" : "text-foreground"}`}>{dueText(o, today)}</span>
      </span>
    </article>
  );
}

// Tabule (PC): sloupec = stav. Kartu jde přetáhnout dál; do „Převzato“ jen přes převzetí s fotkou.
export function OrderBoard({ items, money, canEdit, onOpen, onMove, onReceive, am = "gross" }) {
  const today = todayISO();
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const drop = (e, col) => {
    e.preventDefault(); setOver(null);
    const o = items.find((x) => x.id === (e.dataTransfer.getData("text/plain") || dragId));
    setDragId(null);
    if (!o || columnOf(o)?.key === col.key || o.status === "received") return;
    if (col.key === "received") onReceive(o);
    else onMove(o, col);
  };
  return (
    <div className="grid items-start grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-2" data-testid="orders-board">
      {COLUMNS.map((c) => {
        const cards = items.filter((o) => columnOf(o)?.key === c.key)
          .sort((a, b) => (b.late || !!b.problem) - (a.late || !!a.problem) || (a.due_date || "9999").localeCompare(b.due_date || "9999"));
        const sum = cards.reduce((a, o) => a + (orderAmount(o, am === "both" ? "net" : am) || 0), 0);   // 391: podle Zobrazení › Částky
        return (
          <section key={c.key} data-testid={`orders-col-${c.key}`} aria-label={`${c.title}, ${cards.length}`}
            onDragOver={(e) => { if (canEdit && e.dataTransfer.types.includes("text/plain")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== c.key) setOver(c.key); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }}
            onDrop={(e) => drop(e, c)}
            className={`flex flex-col min-w-0 rounded-[20px] p-2 bg-card/45 dark:bg-card/30 border border-border/60 backdrop-blur-sm transition-shadow motion-reduce:transition-none
              ${over === c.key ? "ring-2 ring-brand/60" : ""}`}>
            <header className="bg-card rounded-[14px] px-3 pt-2.5 pb-3 shadow-[0_1px_2px_rgba(20,24,33,.05)]">
              <div className="flex items-baseline gap-2">
                <b className="text-[15px] font-bold">{c.title}</b>
                <span className="text-[13px] font-bold tabular-nums text-muted-foreground">{cards.length}</span>
              </div>
              {money && <div className="text-[12.5px] text-muted-foreground tabular-nums" title={formatCZK(sum)}>{formatCZKCompact(sum)} {am === "gross" ? "s DPH" : "bez DPH"}</div>}
              <div className="mt-2 h-1 rounded-full bg-secondary overflow-hidden" aria-hidden>
                <i className={`block h-full rounded-full ${c.bar}`} style={{ width: `${c.pct}%` }} />
              </div>
            </header>
            <div className="flex flex-col gap-2 mt-2">
              {cards.map((o) => (
                <Card key={o.id} o={o} today={today} money={money} am={am} drag={canEdit && o.status !== "received"} dragging={dragId === o.id} onOpen={onOpen}
                  onDragStart={(e, x) => { e.dataTransfer.setData("text/plain", x.id); e.dataTransfer.effectAllowed = "move"; setDragId(x.id); }}
                  onDragEnd={() => { setDragId(null); setOver(null); }} />
              ))}
              {!cards.length && <p className="py-6 text-center text-[13px] text-muted-foreground">Tady nic není</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
}
